/**
 * Format a timestamp to a readable date/time string
 * 
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} - Formatted date/time string
 */
export const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  
  // Calculate time differences
  const diffMs = now - date;
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);
  
  // Format based on how recent the timestamp is
  if (diffMins < 60) {
    return diffMins <= 1 ? 'Just now' : `${diffMins} mins ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
};

/**
 * Group tickets by date for timeline visualization
 * 
 * @param {Array} tickets - Array of ticket objects
 * @returns {Object} - Tickets grouped by date
 */
export const groupTicketsByDate = (tickets) => {
  return tickets.reduce((groups, ticket) => {
    const date = new Date(ticket.createdAt).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(ticket);
    return groups;
  }, {});
};

/**
 * Get ticket statistics by category, status, etc.
 * 
 * @param {Array} tickets - Array of ticket objects
 * @returns {Object} - Statistics object
 */
export const getTicketStatistics = (tickets) => {
  const stats = {
    total: tickets.length,
    byStatus: {},
    byCategory: {},
    byPriority: {},
    responseTime: {
      average: 0,
      fastest: Infinity,
      slowest: 0
    }
  };
  
  // Process tickets for stats
  tickets.forEach(ticket => {
    // Count by status
    stats.byStatus[ticket.status] = (stats.byStatus[ticket.status] || 0) + 1;
    
    // Count by category
    stats.byCategory[ticket.category] = (stats.byCategory[ticket.category] || 0) + 1;
    
    // Count by priority
    if (ticket.priority) {
      stats.byPriority[ticket.priority] = (stats.byPriority[ticket.priority] || 0) + 1;
    }
    
    // Calculate response times if applicable
    if (ticket.messages && ticket.messages.length >= 2) {
      const customerMessages = ticket.messages.filter(m => m.senderId === ticket.customer.id);
      const agentMessages = ticket.messages.filter(m => m.senderId !== ticket.customer.id);
      
      if (customerMessages.length > 0 && agentMessages.length > 0) {
        let responseTimes = [];
        
        customerMessages.forEach(custMsg => {
          // Find the next agent message after this customer message
          const custMsgTime = new Date(custMsg.timestamp).getTime();
          const nextAgentMsg = agentMessages.find(agentMsg => {
            return new Date(agentMsg.timestamp).getTime() > custMsgTime;
          });
          
          if (nextAgentMsg) {
            const responseTime = (new Date(nextAgentMsg.timestamp) - new Date(custMsg.timestamp)) / 1000 / 60; // In minutes
            responseTimes.push(responseTime);
          }
        });
        
        if (responseTimes.length > 0) {
          const totalResponseTime = responseTimes.reduce((sum, time) => sum + time, 0);
          stats.responseTime.average = totalResponseTime / responseTimes.length;
          stats.responseTime.fastest = Math.min(...responseTimes);
          stats.responseTime.slowest = Math.max(...responseTimes);
        }
      }
    }
  });
  
  return stats;
};

/**
 * Get customer satisfaction rating from ticket messages
 * 
 * @param {Object} ticket - Ticket object
 * @returns {number|null} - Satisfaction rating (1-5) or null if not rated
 */
export const getCustomerSatisfaction = (ticket) => {
  if (!ticket || !ticket.messages) return null;
  
  // Look for rating messages - this would depend on your actual implementation
  const ratingMessage = ticket.messages.find(msg => 
    msg.senderId === ticket.customer.id && msg.content && msg.content.includes('Rating:')
  );
  
  if (ratingMessage) {
    const match = ratingMessage.content.match(/Rating:\s*(\d+)/i);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
  }
  
  return null;
};
